import { includes, isEqual, mapValues } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import MediaQuery from 'react-responsive';

import { ContentZonesService, LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import {
    ClickOut,
    ShiftDigitalService,
    MEDIA_QUERIES,
    AnalyticsService,
    TRACKING_TAGS as TAGS,
} from '@sm360/widget-commons-general';

import { COLANDER_GROUPS } from 'inventory-listing/constants/colanders.js';
import VehicleCriteriaModel from 'inventory-listing/models/VehicleCriteriaModel.js';
import MetaDataModel from 'inventory-listing/models/MetaDataModel.js';
import ListingFilterHead from 'inventory-listing/components/ListingFilters/Components/ListingFiltersHead/ListingFiltersHead.jsx';
import { getFilters } from 'inventory-listing/services/ListingUsedService/ListingUsedService.js';
import SearchBar from 'inventory-listing/components/SearchBar/SearchBar.jsx';
import ListingFilterBodyStyle from 'inventory-listing/components/ListingFilters/Components/ListingFiltersBodyStyle/ListingFiltersBodyStyle.jsx';
import ListingFilterBrand from 'inventory-listing/components/ListingFilters/Components/ListingFiltersBrand/ListingFiltersBrand.jsx';
import ListingFilterCategory from 'inventory-listing/components/ListingFilters/Components/ListingFiltersCategory/ListingFiltersCategory.jsx';
import ListingFilterDriveTrain from 'inventory-listing/components/ListingFilters/Components/ListingFiltersDriveTrain/ListingFiltersDriveTrain.jsx';
import ListingFilterOdometer from 'inventory-listing/components/ListingFilters/Components/ListingFiltersOdometer/ListingFiltersOdometer.jsx';
import MetadataButtonContainer from 'inventory-listing/components/ListingFilters/Components/MetadataButtonContainer/MetadataButtonContainer.jsx';
import ListingFilterYears from 'inventory-listing/components/ListingFilters/Components/ListingFiltersYears/ListingFiltersYears.jsx';
import ListingFiltersContainer from 'inventory-listing/components/ListingFilters/ListingFiltersContainer.js';
import metadataCategoriesCriteriaNames from 'inventory-listing/constants/metadataCategoriesCriteriaNames.js';
import ListingFiltersPrice from 'inventory-listing/components/ListingFilters/Components/ListingFiltersPrice/ListingFiltersPrice.jsx';
import OrderBy from 'inventory-listing/components/OrderBy/OrderBy.jsx';

import 'inventory-listing/components/ListingFilters/ListingFilters.scss';

class ListingFilters extends React.Component {
    constructor(props) {
        super(props);

        this.applyNewCriteria = this.applyNewCriteria.bind(this);
        this.renderFilters = this.renderFilters.bind(this);

        // Mock until the backend provide us with proper data
        this.driveTrains = [
            new MetaDataModel({
                code: ['ALL_WHEEL_DRIVE', 'FOUR_BY_FOUR'],
                label: LocaleService.t('DRIVE_TRAINS.AWD'),
            }),
            new MetaDataModel({
                code: 'FRONT_WHEEL_DRIVE',
                label: LocaleService.t('DRIVE_TRAINS.FWD'),
            }),
            new MetaDataModel({
                code: 'REAR_WHEEL_DRIVE',
                label: LocaleService.t('DRIVE_TRAINS.RWD'),
            }),
        ];

        this.trackedCriteria = mapValues(metadataCategoriesCriteriaNames, (metadata) => metadata.criteriaKey);
        delete this.trackedCriteria.FRAME_STYLE; // this particular criteria has its own tracking event

        this.state = {
            metadataCategories: props.metadataCategories,
        };
    }

    componentWillReceiveProps(nextProps) {
        const { metadataCategories, vehicleCriteria } = nextProps;
        if (metadataCategories !== this.state.metadataCategories) {
            const updatedMetadataCategories = metadataCategories.map((metadataCategory) => {
                const criteriaKey = metadataCategoriesCriteriaNames[metadataCategory.name];
                if (criteriaKey && metadataCategory.metadatas) {
                    const metadatasUpdated = metadataCategory.metadatas.map((metadata) =>
                        metadata.set('isSelected', includes(vehicleCriteria[criteriaKey], metadata.code))
                    );
                    return {
                        ...metadataCategory,
                        metadatas: metadatasUpdated,
                    };
                }
                return metadataCategory;
            });

            this.props.actions.setMetadatas(updatedMetadataCategories);
            this.setState({ metadataCategories: updatedMetadataCategories });
        }
        if (!isEqual(this.props.vehicleCriteria, nextProps.vehicleCriteria)) {
            ShiftDigitalService.updateListingEvent(
                nextProps.vehicleCriteria,
                this.state.metadataCategories,
                nextProps.vehicleCount
            );
        }
    }

    applyNewCriteria(values, criteria) {
        if (includes(this.trackedCriteria, criteria)) {
            AnalyticsService.gaTrigger(TAGS.LAYER_NAVIGATION__FILTERS(WindowProxy.location().pathname, criteria));
        }
        this.props.updateVehicleCriteria(values, criteria);
    }

    renderFilters() {
        const { vehicleCriteria } = this.props;
        const contentZoneDefaultSort = ContentZonesService.getContentZone('widget.listing.used', 'default.sort');
        let defaultSort = contentZoneDefaultSort && contentZoneDefaultSort.getValue();
        defaultSort = defaultSort || 'PRICE_ASC';
        return (
            <div>
                <ListingFilterHead
                    onFilterClose={this.props.onFilterClose}
                    resetFilters={this.props.updateVehicleCriteria}
                    colanderGroupSlug={this.props.colanderGroupSlug}
                    colanderList={this.props.colanderList}
                    hasNewInventory={this.props.hasNewInventory}
                    hasUsedInventory={this.props.hasUsedInventory}
                />
                <MediaQuery maxWidth={MEDIA_QUERIES.TABLET.MAX}>
                    <OrderBy defaultSort={defaultSort} />
                </MediaQuery>
                <SearchBar textQuery={vehicleCriteria.textQuery} />
                <ListingFilterCategory
                    colanders={this.props.colanders}
                    updateVehicleCriteria={(values, criteria) => {
                        this.applyNewCriteria(values, criteria);
                        this.props.updateVehicleCriteria([], 'makeIds');
                        this.props.updateVehicleCriteria([], 'modelIds');
                    }}
                    selectedColander={vehicleCriteria.colanderSlug}
                />
                <ListingFiltersPrice
                    prices={getFilters(this.state.metadataCategories, 'SALE_PRICE_RANGE')}
                    salePriceRange={vehicleCriteria.salePriceRange}
                    updateVehicleCriteria={this.applyNewCriteria}
                />
                <ListingFilterBodyStyle
                    bodystyles={getFilters(this.state.metadataCategories, 'FRAME_STYLE')}
                    updateVehicleCriteria={this.applyNewCriteria}
                />
                <ListingFilterBrand
                    makes={getFilters(this.state.metadataCategories, 'MAKE')}
                    updateVehicleCriteria={this.applyNewCriteria}
                    vehicleCriteria={vehicleCriteria}
                />
                <ListingFilterYears
                    years={getFilters(this.state.metadataCategories, 'YEAR')}
                    updateVehicleCriteria={this.applyNewCriteria}
                />
                <MetadataButtonContainer
                    metadatas={getFilters(this.state.metadataCategories, 'TRANSMISSION_TYPE')}
                    updateVehicleCriteria={this.applyNewCriteria}
                    groupTitle={LocaleService.t('FILTERS.TRANSMISSION')}
                    filterName="transmissionTypes"
                />
                <MetadataButtonContainer
                    metadatas={getFilters(this.state.metadataCategories, 'FUEL_TYPE')}
                    updateVehicleCriteria={this.applyNewCriteria}
                    groupTitle={LocaleService.t('FILTERS.FUEL_TYPE')}
                    filterName="fuelTypes"
                />
                <ListingFilterOdometer
                    isUsed={this.props.colanderGroupSlug === COLANDER_GROUPS.DEFAULT}
                    odometers={getFilters(this.state.metadataCategories, 'ODOMETER_RANGE')}
                    odometerRange={vehicleCriteria.odometerRange}
                    updateVehicleCriteria={this.applyNewCriteria}
                />
                <ListingFilterDriveTrain driveTrains={this.driveTrains} updateVehicleCriteria={this.applyNewCriteria} />
                {/*
                    <ListingFilterColors
                        colors={this.getFilters('EXTERIOR_COLOR')}
                        updateVehicleCriteria={this.props.updateVehicleCriteria}
                    />
                    */}
                <div className="apply-button-bloc">
                    <button className="apply-button sr-button-2 is-small" onClick={this.props.onFilterClose}>
                        {LocaleService.t('BUTTONS.APPLY')}
                    </button>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className="listing-used-filters">
                <MediaQuery maxWidth={MEDIA_QUERIES.TABLET.MAX}>
                    <ClickOut onClickOut={this.props.onFilterClose}>{this.renderFilters()}</ClickOut>
                </MediaQuery>
                <MediaQuery minWidth={MEDIA_QUERIES.DESKTOP.MIN}>{this.renderFilters()}</MediaQuery>
            </div>
        );
    }
}

ListingFilters.propTypes = {
    onFilterClose: PropTypes.func.isRequired,
    metadataCategories: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            metadatas: PropTypes.arrayOf(MetaDataModel).isRequired,
        })
    ).isRequired,
    updateVehicleCriteria: PropTypes.func.isRequired,
    colanders: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            slug: PropTypes.string,
        })
    ).isRequired,
    colanderList: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            slug: PropTypes.string,
        })
    ).isRequired,
    vehicleCriteria: PropTypes.instanceOf(VehicleCriteriaModel).isRequired,
    actions: PropTypes.shape({
        // eslint-disable-line react/no-unused-prop-types
        setMetadatas: PropTypes.func.isRequired,
    }).isRequired,
    vehicleCount: PropTypes.number.isRequired, // eslint-disable-line react/no-unused-prop-types
    colanderGroupSlug: PropTypes.string.isRequired,
    hasUsedInventory: PropTypes.bool.isRequired,
    hasNewInventory: PropTypes.bool.isRequired,
};

ListingFilters.displayName = 'ListingFilters';

export { ListingFilters };
export default ListingFiltersContainer(ListingFilters);



// WEBPACK FOOTER //
// ./client/src/components/ListingFilters/ListingFilters.jsx